import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpRight, Phone, Mail, MapPin, Check } from "lucide-react";
import { toast } from "sonner";
import { CONTACT } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const productOptions = [
  "SCE-HR+ Anti Moisture Powder",
  "SCE-DM Desiccant Masterbatch",
  "SCE-QL Quick Lime Powder",
  "SCE-CF Calcium Filler",
  "SCE-CMB Colour Masterbatch",
  "General inquiry",
];

const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    product_interest: productOptions[0],
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Name, email and message are required.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("Inquiry sent. We'll reply within 24 hours.");
      setSent(true);
      setForm({
        name: "",
        company: "",
        email: "",
        phone: "",
        product_interest: productOptions[0],
        message: "",
      });
    } catch (err) {
      toast.error("Something went wrong. Please try again or call us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid={CONTACT.section}
      className="bg-[#0A2A1E] text-[#FAF9F6] py-24 lg:py-40 px-5 lg:px-10 relative overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        {/* Left */}
        <div className="lg:col-span-6">
          <div className="section-rule overline text-[#C6F035] mb-6">
            Get in Touch
          </div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="font-display text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-[-0.02em]"
          >
            Let's <span className="font-display-italic text-[#C6F035]">build</span>
            <br />
            something clean.
          </motion.h2>

          <p className="mt-8 text-[#FAF9F6]/70 max-w-md text-base md:text-lg leading-relaxed">
            Send us an inquiry, request a technical datasheet, or place a
            sample order. Volume discounts and tailored dosages available.
          </p>

          <div className="mt-14 space-y-8">
            <div className="flex items-start gap-4">
              <div className="mt-1 p-2 border border-[#C6F035]/40">
                <Phone size={14} className="text-[#C6F035]" />
              </div>
              <div>
                <div className="overline text-[#FAF9F6]/50 mb-1">Call</div>
                <div className="text-lg">
                  <a href="tel:+917296920646" className="link-wipe">
                    +91 72969 20646
                  </a>
                  <span className="mx-2 text-[#FAF9F6]/40">/</span>
                  <a href="tel:+916377429079" className="link-wipe">
                    +91 63774 29079
                  </a>
                </div>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="mt-1 p-2 border border-[#C6F035]/40">
                <Mail size={14} className="text-[#C6F035]" />
              </div>
              <div>
                <div className="overline text-[#FAF9F6]/50 mb-1">Write</div>
                <a
                  href="mailto:sconecoreenterprises@gmail.com"
                  className="link-wipe text-lg break-all"
                >
                  sconecoreenterprises@gmail.com
                </a>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="mt-1 p-2 border border-[#C6F035]/40">
                <MapPin size={14} className="text-[#C6F035]" />
              </div>
              <div className="space-y-5">
                <div>
                  <div className="overline text-[#FAF9F6]/50 mb-1">
                    Unit I · Udaipur
                  </div>
                  <p className="text-lg leading-snug max-w-xs">
                    F-177, Gudli Industrial Area,<br />
                    Udaipur, Rajasthan — 313001, India
                  </p>
                </div>
                <div>
                  <div className="overline text-[#FAF9F6]/50 mb-1">
                    Unit II · Bhiwadi
                  </div>
                  <p className="text-lg leading-snug max-w-xs">
                    H1-230, Chopanki Industrial Area,<br />
                    Bhiwadi, Alwar (NCR),<br />
                    Rajasthan — 301019, India
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right — Form */}
        <div className="lg:col-span-6 lg:pl-8">
          <form
            data-testid={CONTACT.form}
            onSubmit={handleSubmit}
            className="space-y-8 pt-2"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="form-label">Name*</label>
                <input
                  id="name"
                  data-testid={CONTACT.name}
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Arnav Malkani"
                  className="form-input"
                  required
                />
              </div>
              <div>
                <label htmlFor="company" className="form-label">Company</label>
                <input
                  id="company"
                  data-testid={CONTACT.company}
                  name="company"
                  value={form.company}
                  onChange={handleChange}
                  placeholder="Your company"
                  className="form-input"
                />
              </div>
              <div>
                <label htmlFor="email" className="form-label">Email*</label>
                <input
                  id="email"
                  data-testid={CONTACT.email}
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="you@company.com"
                  className="form-input"
                  required
                />
              </div>
              <div>
                <label htmlFor="phone" className="form-label">Phone</label>
                <input
                  id="phone"
                  data-testid={CONTACT.phone}
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="+91 …"
                  className="form-input"
                />
              </div>
            </div>

            <div>
              <label htmlFor="product_interest" className="form-label">
                Product of Interest
              </label>
              <select
                id="product_interest"
                data-testid={CONTACT.interest}
                name="product_interest"
                value={form.product_interest}
                onChange={handleChange}
                className="form-input appearance-none cursor-pointer"
                style={{ colorScheme: "dark" }}
              >
                {productOptions.map((o) => (
                  <option key={o} value={o} className="bg-[#0A2A1E]">
                    {o}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="message" className="form-label">Message*</label>
              <textarea
                id="message"
                data-testid={CONTACT.message}
                name="message"
                value={form.message}
                onChange={handleChange}
                rows={4}
                placeholder="MOQ, technical requirements, dosage, delivery city…"
                className="form-input resize-none"
                required
              />
            </div>

            <div className="pt-4 flex items-center gap-6 flex-wrap">
              <button
                type="submit"
                data-testid={CONTACT.submit}
                disabled={loading}
                className="btn-primary disabled:opacity-60"
              >
                {loading ? "Sending…" : "Send Inquiry"}
                <ArrowUpRight size={14} />
              </button>
              {sent && (
                <span
                  data-testid={CONTACT.success}
                  className="inline-flex items-center gap-2 overline text-[#C6F035]"
                >
                  <Check size={14} /> Message received
                </span>
              )}
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
